import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { toNumberOrNull } from '../utils/formatters';

const saleStorageKey = 'salesStatsSelectedSaleId';
const DEFAULT_BREAKDOWN_SORT = Object.freeze({ key: 'remaining_value', direction: 'desc' });
const DISCOUNT_SLIDER_MIN = 0;
const DISCOUNT_SLIDER_MAX = 100;
const DISCOUNT_SLIDER_STEP = 5;
const VALUE_SECTION_TITLE = 'Current Value Snapshot';
const TOP_UNSOLD_CATEGORY_MIN_WIDTH = 140;
const TOP_UNSOLD_CATEGORY_MAX_WIDTH = 360;
const TOP_UNSOLD_CATEGORY_FONT = '600 16px Inter, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
const TOP_UNSOLD_CATEGORY_EXTRA_PADDING = 18;
const HELP_TEXT = {
  page: 'This page shows what your sale is worth, what is still available, and where the remaining value is sitting by category and section. Use it to decide what to discount, where to focus, and what may need specialist attention.',
  currentSnapshot: 'This section shows the value of items currently available for sale at today\'s discount. It does not include sold items or items placed on hold.',
  currentValue: 'The total current value of all items still available for sale after today\'s discount is applied.',
  top10Value: 'The combined current value of the 10 highest-value available items. This helps show whether your remaining value is tied up in a few big items or spread across the house.',
  highestSection: 'The section with the most current available value still left in the sale. Use this to see where the biggest physical backlog is.',
  highestCategory: 'The category with the most current available value still left in the sale. Use this to see what kind of inventory is dominating the sale.',
  overallTotals: 'This section shows the full sale at the system level, including total inventory value, what is still available, what has sold, and what is on hold.',
  totalInventoryValue: 'The total tagged value of all items in the sale. This includes available, sold, and on-hold items.',
  availableTagValue: 'The tagged value of items still available for sale. This is before today\'s discount is applied.',
  salesRevenue: 'The total money already sold through the sale. This reflects actual sold prices, not tagged prices.',
  sellThrough: 'The percentage of total sale items that have already sold. Higher sell-through means the sale is clearing faster.',
  onHoldValue: 'The value of items removed from active selling but still kept in the sale record. These items are not currently on the floor.',
  categoryBreakdown: 'This table shows what has sold and what remains by category using tagged prices. Use it to see which types of items still hold the most value.',
  sectionBreakdown: 'This table shows what has sold and what remains by section using tagged prices. Use it to see where value is still sitting in the sale physically.',
  soldCol: 'The number of items sold in this category.',
  activeCol: 'The number of items still active and available in this category.',
  sellThroughCol: 'The percentage of items in this category that have sold. Higher percentages mean the category is moving faster.',
  tagValueCol: 'The total tagged price of all items in this category.',
  salesRevenueCol: 'The total money sold from this category at actual sold prices.',
  unsoldTaggedCol: 'The tagged value of items still unsold in this category. This is before today\'s discount.',
  avgTagPriceCol: 'The average tagged price of items in this category.',
  sectionCol: 'The physical area or grouped location where items are assigned in the sale.',
  top5Section: 'Expand a category to see the highest-value unsold items still left in that category. Use this to spot the items most worth reviewing, promoting, or repricing.',
  rankCol: 'The item\'s position within that category based on tagged value, from highest to lower.',
  itemTitleCol: 'The inventory title currently assigned to the item.',
  tagPriceCol: 'The item\'s tagged price before any sale discount is applied.',
  top5ItemSectionCol: 'The section where that item is currently assigned in the sale.',
};

function toggleSortState(prev, key, nameKey) {
  if (prev.key === key) {
    return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: key === nameKey ? 'asc' : 'desc' };
}

function getHeaderColorClass(activeSortKey, key, emphasized = false) {
  if (activeSortKey === key) {
    return emphasized ? 'bg-slate-100 text-slate-900' : 'bg-slate-100 text-slate-700';
  }
  return emphasized ? 'bg-slate-50 text-slate-900' : 'bg-slate-50 text-slate-600';
}

function getCellColorClass(activeSortKey, key, emphasized = false) {
  if (activeSortKey === key) {
    return emphasized ? 'bg-slate-100 text-slate-900 font-semibold' : 'bg-slate-100 text-slate-700';
  }
  return emphasized ? 'text-slate-900 font-semibold' : 'text-slate-700';
}

function sortBreakdownRows(rows, sortState, nameKey, toNum) {
  const { key, direction } = sortState;
  const multiplier = direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (key === nameKey) {
      cmp = String(a?.[nameKey] || '').localeCompare(String(b?.[nameKey] || ''), undefined, { sensitivity: 'base' });
    } else {
      cmp = toNum(a?.[key], 0) - toNum(b?.[key], 0);
    }
    if (cmp !== 0) return cmp * multiplier;

    const unsoldDiff = toNum(b?.remaining_value, 0) - toNum(a?.remaining_value, 0);
    if (unsoldDiff !== 0) return unsoldDiff;
    return String(a?.[nameKey] || '').localeCompare(String(b?.[nameKey] || ''), undefined, { sensitivity: 'base' });
  });
}

function measureTextColumnWidth(labels, options = {}) {
  const {
    min = 120,
    max = Number.POSITIVE_INFINITY,
    charWidth = 9,
    font = '600 15.3px Inter, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    extraPadding = 20,
  } = options;

  if (typeof document === 'undefined') {
    const longestChars = labels.reduce((maxLen, label) => Math.max(maxLen, label.length), 0);
    return Math.min(max, Math.max(min, Math.ceil(longestChars * charWidth)));
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const longestChars = labels.reduce((maxLen, label) => Math.max(maxLen, label.length), 0);
    return Math.min(max, Math.max(min, Math.ceil(longestChars * charWidth)));
  }

  ctx.font = font;
  const widestLabelPx = labels.reduce((maxPx, label) => Math.max(maxPx, ctx.measureText(label).width), 0);
  return Math.min(max, Math.max(min, Math.ceil(widestLabelPx + extraPadding)));
}

function asFixedWidthStyle(widthPx) {
  return {
    width: `${widthPx}px`,
    minWidth: `${widthPx}px`,
    maxWidth: `${widthPx}px`,
  };
}

function useMeasuredTableWidth(enabled, tableRef, watchKey) {
  const [measuredWidth, setMeasuredWidth] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const tableEl = tableRef.current;
    if (!tableEl) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.ceil(tableEl.scrollWidth || 0);
      if (!Number.isFinite(nextWidth) || nextWidth <= 0) return;
      setMeasuredWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(tableEl);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [enabled, tableRef, watchKey]);

  return measuredWidth;
}

function safeAverage(total, count) {
  if (!Number.isFinite(count) || count <= 0) return 0;
  return total / count;
}

function formatDiscountSupportText(pct) {
  return pct <= 0 ? 'Tag' : `At ${pct}% discount`;
}

function getTop10ValueGuidance(top10SharePct) {
  if (top10SharePct <= 15) return 'Value spread across house';
  if (top10SharePct <= 30) return 'Most value is spread out';
  if (top10SharePct <= 45) return 'Some value in bigger items';
  if (top10SharePct <= 60) return 'Big items need attention';
  return 'Sale depends on top items';
}

function getSalePatternInterpretation(top10SharePct) {
  if (top10SharePct < 20) {
    return 'Most of the remaining value is spread across the sale, so a broader discount may help move more inventory.';
  }
  if (top10SharePct < 40) {
    return 'Some of the remaining value is in the top items, but the rest of the sale still matters.';
  }
  if (top10SharePct < 60) {
    return 'A large share of remaining value is tied up in a small number of items. Review those first before lowering prices across the whole sale.';
  }
  return 'Most of the remaining value is tied up in a few top items. Focus on those first before discounting the whole sale.';
}

function pickHighestValueRow(rows, { nameKey }, toNum) {
  const availableRows = (rows || []).filter((row) => toNum(row?.active_item_count, 0) > 0);
  if (!availableRows.length) return null;

  return availableRows.reduce((best, row) => {
    if (!best) return row;

    const bestValue = toNum(best?.remaining_value, 0);
    const rowValue = toNum(row?.remaining_value, 0);
    if (rowValue !== bestValue) return rowValue > bestValue ? row : best;

    const bestCount = toNum(best?.active_item_count, 0);
    const rowCount = toNum(row?.active_item_count, 0);
    if (rowCount !== bestCount) return rowCount > bestCount ? row : best;

    const bestName = String(best?.[nameKey] || '');
    const rowName = String(row?.[nameKey] || '');
    return rowName.localeCompare(bestName, undefined, { sensitivity: 'base' }) < 0 ? row : best;
  }, null);
}

function HelpTip({ text, className = '', align = 'start' }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 288 });

  const updatePopoverPos = useCallback(() => {
    const anchor = wrapperRef.current;
    if (!anchor || typeof window === 'undefined') return;

    const rect = anchor.getBoundingClientRect();
    const maxWidth = Math.min(288, Math.floor(window.innerWidth * 0.8));
    const viewportPadding = 8;
    const unclampedLeft = align === 'end' ? (rect.right - maxWidth) : rect.left;
    const maxLeft = window.innerWidth - maxWidth - viewportPadding;
    const left = Math.min(Math.max(unclampedLeft, viewportPadding), Math.max(viewportPadding, maxLeft));
    const top = rect.bottom + 8;

    setPopoverPos({ top, left, width: maxWidth });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    updatePopoverPos();
    const onWindowChange = () => updatePopoverPos();

    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onWindowChange, true);
    return () => {
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onWindowChange, true);
    };
  }, [open, updatePopoverPos]);

  const onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const onTriggerKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const onBlurCapture = (event) => {
    if (!wrapperRef.current) return;
    if (wrapperRef.current.contains(event.relatedTarget)) return;
    setOpen(false);
  };

  return (
    <span
      ref={wrapperRef}
      className={`relative inline-flex align-middle ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlurCapture={onBlurCapture}
    >
      <span
        role="button"
        tabIndex={0}
        aria-label="Help"
        aria-expanded={open}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-400 text-[11px] leading-none font-semibold text-slate-600 bg-white select-none cursor-pointer"
      >
        ?
      </span>
      {open && createPortal(
        <span
          role="tooltip"
          className="fixed z-[2200] rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700 shadow-lg"
          style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px`, width: `${popoverPos.width}px` }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

function CollapseChevron({ expanded }) {
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
      aria-hidden="true"
    >
      <svg
        className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M5.5 7.5L10 12.5L14.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function SalesStats() {
  const { user, fetchWrapper, loading: authLoading, ready } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id || null;

  const [sales, setSales] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState('');
  const [overview, setOverview] = useState(null);
  const [categoryRows, setCategoryRows] = useState([]);
  const [sectionRows, setSectionRows] = useState([]);
  const [topUnsoldRows, setTopUnsoldRows] = useState([]);
  const [topUnsoldShare, setTopUnsoldShare] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState('');
  const [categoryExpanded, setCategoryExpanded] = useState(true);
  const [sectionExpanded, setSectionExpanded] = useState(false);
  const [categorySort, setCategorySort] = useState(DEFAULT_BREAKDOWN_SORT);
  const [sectionSort, setSectionSort] = useState(DEFAULT_BREAKDOWN_SORT);
  const [proposedDiscountPct, setProposedDiscountPct] = useState(0);
  const [selectedCategoryRowKey, setSelectedCategoryRowKey] = useState(null);
  const [selectedSectionRowKey, setSelectedSectionRowKey] = useState(null);
  const [topUnsoldExpanded, setTopUnsoldExpanded] = useState(false);
  const [discountCalculatorExpanded, setDiscountCalculatorExpanded] = useState(false);
  const [saleMenuOpen, setSaleMenuOpen] = useState(false);
  const [unsoldAccordionExpanded, setUnsoldAccordionExpanded] = useState({});
  const categoryTableMeasureRef = useRef(null);
  const sectionTableMeasureRef = useRef(null);
  const saleMenuRef = useRef(null);
  const statsRequestIdRef = useRef(0);

  const toNum = useCallback((value, fallback = 0) => {
    const n = toNumberOrNull(value);
    return n === null ? fallback : n;
  }, []);

  const formatCount = useCallback((value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return String(Math.trunc(n));
  }, []);

  const formatCurrency = useCallback((value) => {
    const n = toNum(value, 0);
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [toNum]);

  const formatPercent = useCallback((value) => {
    const n = toNum(value, 0);
    return `${n.toFixed(2)}%`;
  }, [toNum]);

  const formatShortDate = useCallback((value) => {
    const ts = Date.parse(value || '');
    if (!Number.isFinite(ts)) return null;
    return new Date(ts).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  const rpcJson = useCallback(async (endpoint, body) => {
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
    }

    return response.json();
  }, [fetchWrapper]);

  const fetchSales = useCallback(async () => {
    if (!userId) return;
    setLoadingSales(true);
    setError('');
    try {
      const data = await rpcJson('select_all_sales', { p_client_id: userId });
      const list = (Array.isArray(data) ? data : []).filter((sale) => sale?.status !== 'deleted');
      const sorted = [...list].sort((a, b) => {
        const aTs = Date.parse(a?.start_date || '');
        const bTs = Date.parse(b?.start_date || '');
        const aValid = Number.isFinite(aTs);
        const bValid = Number.isFinite(bTs);
        if (aValid && bValid) return bTs - aTs; // newest start_date first
        if (aValid) return -1;
        if (bValid) return 1;
        return String(a?.sale_name || '').localeCompare(String(b?.sale_name || ''), undefined, { sensitivity: 'base' });
      });
      setSales(sorted);
    } catch (err) {
      console.error('SalesStats fetchSales error:', err);
      setError(`Failed to load sales: ${err.message}`);
      setSales([]);
    } finally {
      setLoadingSales(false);
    }
  }, [rpcJson, userId]);

  const fetchStats = useCallback(async (saleId) => {
    const requestId = statsRequestIdRef.current + 1;
    statsRequestIdRef.current = requestId;
    const isStale = () => statsRequestIdRef.current !== requestId;

    if (!saleId || !userId) {
      setOverview(null);
      setCategoryRows([]);
      setSectionRows([]);
      setTopUnsoldRows([]);
      setTopUnsoldShare(null);
      return;
    }

    setLoadingStats(true);
    setError('');

    try {
      const [overviewData, categoriesData, sectionsData] = await Promise.all([
        rpcJson('get_sale_stats_overview', { p_sale_id: saleId, p_client_id: userId }),
        rpcJson('get_sale_category_stats', { p_sale_id: saleId, p_client_id: userId }),
        rpcJson('get_sale_section_stats', { p_sale_id: saleId, p_client_id: userId }),
      ]);

      const singleOverview = Array.isArray(overviewData)
        ? (overviewData[0] || null)
        : (overviewData || null);

      if (isStale()) return;
      setOverview(singleOverview);
      setCategoryRows(Array.isArray(categoriesData) ? categoriesData : []);
      setSectionRows(Array.isArray(sectionsData) ? sectionsData : []);
      setTopUnsoldRows([]);
      setTopUnsoldShare(null);

      // Optional Phase 2 RPCs: keep page usable if these functions are not yet deployed.
      try {
        const [topUnsoldData, shareData] = await Promise.all([
          rpcJson('get_sale_top_unsold_items_by_category', { p_sale_id: saleId, p_client_id: userId }),
          rpcJson('get_sale_top_unsold_share', { p_sale_id: saleId, p_client_id: userId }),
        ]);
        const singleShare = Array.isArray(shareData)
          ? (shareData[0] || null)
          : (shareData || null);
        if (isStale()) return;
        setTopUnsoldRows(Array.isArray(topUnsoldData) ? topUnsoldData : []);
        setTopUnsoldShare(singleShare);
      } catch (optionalErr) {
        if (isStale()) return;
        console.warn('Optional SalesStats RPCs unavailable:', optionalErr?.message || optionalErr);
      }
    } catch (err) {
      if (isStale()) return;
      console.error('SalesStats fetchStats error:', err);
      setError(`Failed to load sale stats: ${err.message}`);
      setOverview(null);
      setCategoryRows([]);
      setSectionRows([]);
      setTopUnsoldRows([]);
      setTopUnsoldShare(null);
    } finally {
      if (isStale()) return;
      setLoadingStats(false);
    }
  }, [rpcJson, userId]);

  useEffect(() => {
    if (authLoading || !ready || !userId) return;
    fetchSales();
  }, [authLoading, fetchSales, ready, userId]);

  useEffect(() => {
    if (!sales.length) {
      setSelectedSaleId('');
      return;
    }

    if (selectedSaleId && sales.some((sale) => sale.id === selectedSaleId)) {
      return;
    }

    const savedSaleId = sessionStorage.getItem(saleStorageKey);
    if (savedSaleId && sales.some((sale) => sale.id === savedSaleId)) {
      setSelectedSaleId(savedSaleId);
      return;
    }

    setSelectedSaleId(sales[0].id);
  }, [sales, selectedSaleId]);

  useEffect(() => {
    if (!selectedSaleId) return;
    sessionStorage.setItem(saleStorageKey, selectedSaleId);
  }, [selectedSaleId]);

  useEffect(() => {
    if (!selectedSaleId) return;
    fetchStats(selectedSaleId);
  }, [fetchStats, selectedSaleId]);

  useEffect(() => {
    if (!saleMenuOpen) return undefined;

    const onPointerDown = (event) => {
      if (!saleMenuRef.current) return;
      if (!saleMenuRef.current.contains(event.target)) {
        setSaleMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSaleMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [saleMenuOpen]);

  useEffect(() => {
    // Keep the new accordion collapsed by default on each sale/data refresh.
    setUnsoldAccordionExpanded({});
  }, [selectedSaleId, topUnsoldRows]);

  const selectedSale = useMemo(
    () => sales.find((sale) => sale.id === selectedSaleId) || null,
    [sales, selectedSaleId]
  );

  const saleSelectWidthCh = useMemo(() => {
    const labels = sales.length
      ? sales.map((sale) => String(sale?.sale_name || '').trim())
      : ['No sales available'];
    const longestChars = labels.reduce((maxLen, label) => Math.max(maxLen, label.length), 0);
    return Math.min(72, Math.max(18, longestChars + 4));
  }, [sales]);

  const visibleCategoryRows = useMemo(() => {
    const rows = categoryRows.filter((row) => toNum(row?.item_count, 0) > 0);
    return sortBreakdownRows(rows, categorySort, 'category', toNum);
  }, [categoryRows, categorySort, toNum]);

  const visibleSectionRows = useMemo(() => {
    const rows = sectionRows.filter((row) => toNum(row?.item_count, 0) > 0);
    return sortBreakdownRows(rows, sectionSort, 'section_name', toNum);
  }, [sectionRows, sectionSort, toNum]);

  const categoryColumnWidthPx = useMemo(() => {
    const labels = [
      'Category',
      ...visibleCategoryRows.map((row) => String(row?.category || '').trim()),
    ];
    return measureTextColumnWidth(labels, {
      min: 120,
      charWidth: 9,
      font: '600 15.3px Inter, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      extraPadding: 20,
    });
  }, [visibleCategoryRows]);

  const sectionColumnWidthPx = useMemo(() => {
    const labels = [
      'Section',
      ...visibleSectionRows.map((row) => String(row?.section_name || '').trim()),
    ];
    return measureTextColumnWidth(labels, {
      min: 120,
      charWidth: 9,
      font: '600 15.3px Inter, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      extraPadding: 20,
    });
  }, [visibleSectionRows]);

  const categoryColumnWidthStyle = useMemo(() => asFixedWidthStyle(categoryColumnWidthPx), [categoryColumnWidthPx]);
  const sectionColumnWidthStyle = useMemo(() => asFixedWidthStyle(sectionColumnWidthPx), [sectionColumnWidthPx]);

  const categoryMeasureWatchKey = `${categoryColumnWidthPx}|${loadingStats ? 1 : 0}|${selectedSaleId || ''}`;
  const sectionMeasureWatchKey = `${sectionColumnWidthPx}|${loadingStats ? 1 : 0}|${selectedSaleId || ''}`;
  const categoryGridMeasuredWidth = useMeasuredTableWidth(categoryExpanded, categoryTableMeasureRef, categoryMeasureWatchKey);
  const sectionGridMeasuredWidth = useMeasuredTableWidth(sectionExpanded, sectionTableMeasureRef, sectionMeasureWatchKey);

  const measuredWorkingWidth = useMemo(() => {
    const categoryWidth = Number.isFinite(categoryGridMeasuredWidth) ? categoryGridMeasuredWidth : 0;
    const sectionWidth = Number.isFinite(sectionGridMeasuredWidth) ? sectionGridMeasuredWidth : 0;
    const maxWidth = Math.max(categoryWidth, sectionWidth);
    return maxWidth > 0 ? maxWidth : 0;
  }, [categoryGridMeasuredWidth, sectionGridMeasuredWidth]);

  const workingContentStyle = useMemo(() => {
    if (!measuredWorkingWidth) return undefined;
    return { width: `min(100%, ${measuredWorkingWidth}px)` };
  }, [measuredWorkingWidth]);

  const workingPanelStyle = useMemo(() => {
    if (!measuredWorkingWidth) return undefined;
    return { width: `min(100%, calc(${measuredWorkingWidth}px + 3rem))` };
  }, [measuredWorkingWidth]);

  const toggleCategorySort = useCallback((key) => {
    setCategorySort((prev) => toggleSortState(prev, key, 'category'));
  }, []);

  const toggleSectionSort = useCallback((key) => {
    setSectionSort((prev) => toggleSortState(prev, key, 'section_name'));
  }, []);

  const renderSortArrow = useCallback((activeSort, key) => {
    if (activeSort.key !== key) return null;
    return <span className="text-[10px] text-slate-500">{activeSort.direction === 'asc' ? '▲' : '▼'}</span>;
  }, []);

  const categoryHeaderColorClass = useCallback((key, emphasized = false) => {
    return getHeaderColorClass(categorySort.key, key, emphasized);
  }, [categorySort.key]);

  const sectionHeaderColorClass = useCallback((key, emphasized = false) => {
    return getHeaderColorClass(sectionSort.key, key, emphasized);
  }, [sectionSort.key]);

  const categoryCellColorClass = useCallback((key, emphasized = false) => {
    return getCellColorClass(categorySort.key, key, emphasized);
  }, [categorySort.key]);

  const sectionCellColorClass = useCallback((key, emphasized = false) => {
    return getCellColorClass(sectionSort.key, key, emphasized);
  }, [sectionSort.key]);

  const rollupTotals = useMemo(() => {
    const rollupRows = (categoryRows || []).filter((row) => (
      toNum(row?.item_count, 0) > 0
      || toNum(row?.active_item_count, 0) > 0
      || toNum(row?.sold_item_count, 0) > 0
    ));

    if (!rollupRows.length) {
      return {
        hasRollup: false,
        totalItems: 0,
        soldItems: 0,
        activeItems: 0,
        totalTagValue: 0,
        soldValue: 0,
        unsoldTagValue: 0,
      };
    }

    const soldItems = rollupRows.reduce((sum, row) => sum + toNum(row?.sold_item_count, 0), 0);
    const activeItems = rollupRows.reduce((sum, row) => sum + toNum(row?.active_item_count, 0), 0);

    return {
      hasRollup: true,
      totalItems: soldItems + activeItems,
      soldItems,
      activeItems,
      totalTagValue: rollupRows.reduce((sum, row) => sum + toNum(row?.projected_value, 0), 0),
      soldValue: rollupRows.reduce((sum, row) => sum + toNum(row?.sold_value, 0), 0),
      unsoldTagValue: rollupRows.reduce((sum, row) => sum + toNum(row?.remaining_value, 0), 0),
    };
  }, [categoryRows, toNum]);

  const saleMetrics = useMemo(() => {
    const onHoldItemsCount = (() => {
      const candidates = [
        toNum(selectedSale?.on_hold_item_count, Number.NaN),
        toNum(overview?.on_hold_item_count, Number.NaN),
      ].filter((value) => Number.isFinite(value));
      if (!candidates.length) return 0;
      return Math.max(Math.trunc(candidates[0]), 0);
    })();

    const onHoldValue = (() => {
      const candidates = [
        toNum(selectedSale?.on_hold_value, Number.NaN),
        toNum(overview?.on_hold_value, Number.NaN),
      ].filter((value) => Number.isFinite(value));
      if (!candidates.length) return 0;
      return Math.max(candidates[0], 0);
    })();

    const totalSoldValue = (() => {
      const candidates = [
        toNum(selectedSale?.total_sold_price, Number.NaN),
      ].filter((value) => Number.isFinite(value));
      if (candidates.length) return candidates[0];
      if (rollupTotals.hasRollup) return rollupTotals.soldValue;
      const overviewSoldValueCandidates = [
        toNum(overview?.sold_value, Number.NaN),
        toNum(overview?.total_sold_value, Number.NaN),
      ].filter((v) => Number.isFinite(v));
      if (overviewSoldValueCandidates.length) return overviewSoldValueCandidates[0];
      return (sectionRows || []).reduce((sum, row) => sum + toNum(row?.sold_value, 0), 0);
    })();

    const soldItemsCount = rollupTotals.hasRollup
      ? rollupTotals.soldItems
      : toNum(overview?.sold_item_count, 0);

    const totalItemsCount = rollupTotals.hasRollup
      ? Math.max(Math.trunc(rollupTotals.totalItems + onHoldItemsCount), 0)
      : (() => {
          const candidates = [
            toNum(selectedSale?.item_count, Number.NaN),
            toNum(overview?.item_count, Number.NaN),
          ].filter((value) => Number.isFinite(value));
          if (candidates.length) return Math.max(Math.trunc(candidates[0]), 0);
          const activeCount = (categoryRows || []).reduce(
            (sum, row) => sum + toNum(row?.active_item_count, 0),
            0,
          );
          return Math.max(Math.trunc(soldItemsCount + onHoldItemsCount + activeCount), 0);
        })();

    const itemsAvailable = rollupTotals.hasRollup
      ? Math.max(Math.trunc(rollupTotals.activeItems), 0)
      : (() => {
          const candidates = [
            toNum(topUnsoldShare?.total_unsold_count, Number.NaN),
            toNum(overview?.active_item_count, Number.NaN),
            (categoryRows || []).reduce((sum, row) => sum + toNum(row?.active_item_count, 0), 0),
            Math.max(toNum(overview?.item_count, 0) - toNum(overview?.sold_item_count, 0), 0),
          ].filter((value) => Number.isFinite(value));
          if (!candidates.length) return 0;
          return Math.max(Math.trunc(candidates[0]), 0);
        })();

    const sellThroughPct = (
      Number.isFinite(soldItemsCount)
      && Number.isFinite(totalItemsCount)
      && totalItemsCount > 0
    )
      ? ((soldItemsCount / totalItemsCount) * 100)
      : 0;

    return {
      totalSoldValue,
      soldItemsCount,
      totalItemsCount,
      itemsAvailable,
      sellThroughPct,
      onHoldValue,
      onHoldItemsCount,
    };
  }, [categoryRows, overview, rollupTotals, sectionRows, selectedSale, toNum, topUnsoldShare]);

  const {
    totalSoldValue,
    soldItemsCount,
    totalItemsCount,
    itemsAvailable,
    sellThroughPct,
    onHoldValue,
    onHoldItemsCount,
  } = saleMetrics;

  const discountPct = useMemo(() => {
    const pct = toNum(selectedSale?.disc_pct, 0);
    return Math.min(Math.max(pct, 0), 100);
  }, [selectedSale, toNum]);

  const discountSliderMin = DISCOUNT_SLIDER_MIN;
  const discountSliderMax = DISCOUNT_SLIDER_MAX;
  const discountSliderStep = DISCOUNT_SLIDER_STEP;
  const roundedCurrentDiscountPct = useMemo(() => Math.round(discountPct), [discountPct]);
  const currentDiscountAnchorPct = useMemo(() => {
    return Math.min(Math.max(roundedCurrentDiscountPct, discountSliderMin), discountSliderMax);
  }, [discountSliderMax, discountSliderMin, roundedCurrentDiscountPct]);
  const currentDiscountSelectionPct = useMemo(() => {
    const snapped = Math.round(currentDiscountAnchorPct / discountSliderStep) * discountSliderStep;
    return Math.min(Math.max(snapped, discountSliderMin), discountSliderMax);
  }, [currentDiscountAnchorPct, discountSliderMax, discountSliderMin, discountSliderStep]);

  const setTryDiscountPct = useCallback((value) => {
    const parsed = Number(value) || 0;
    const snapped = Math.round(parsed / discountSliderStep) * discountSliderStep;
    const clamped = Math.min(Math.max(snapped, discountSliderMin), discountSliderMax);
    setProposedDiscountPct(clamped);
  }, [discountSliderMax, discountSliderMin, discountSliderStep]);

  useEffect(() => {
    setTryDiscountPct(currentDiscountSelectionPct);
  }, [currentDiscountSelectionPct, selectedSaleId, setTryDiscountPct]);

  const liveValueMultiplier = useMemo(() => {
    return Math.max(0, 1 - (discountPct / 100));
  }, [discountPct]);

  const availableFloorTaggedValue = useMemo(() => {
    if (rollupTotals.hasRollup) return rollupTotals.unsoldTagValue;
    const rpcValue = toNum(topUnsoldShare?.total_remaining_value, Number.NaN);
    if (Number.isFinite(rpcValue)) return rpcValue;
    return (sectionRows || []).reduce((sum, row) => sum + toNum(row?.remaining_value, 0), 0);
  }, [rollupTotals, sectionRows, toNum, topUnsoldShare]);

  const totalLiveValueToday = useMemo(() => {
    return availableFloorTaggedValue * liveValueMultiplier;
  }, [availableFloorTaggedValue, liveValueMultiplier]);

  const proposedDiscountMultiplier = useMemo(() => {
    return Math.max(0, 1 - (proposedDiscountPct / 100));
  }, [proposedDiscountPct]);

  const estimatedValueAtProposedDiscount = useMemo(() => {
    return availableFloorTaggedValue * proposedDiscountMultiplier;
  }, [availableFloorTaggedValue, proposedDiscountMultiplier]);

  const discountPresetButtons = useMemo(() => {
    const fixed = [25, 50, 75]
      .filter((v) => v <= discountSliderMax && v !== currentDiscountSelectionPct);
    return [
      { label: 'Keep current', value: currentDiscountSelectionPct },
      ...fixed.map((v) => ({ label: `${v}%`, value: v })),
    ];
  }, [currentDiscountSelectionPct, discountSliderMax]);

  const currentDiscountMarkerPct = useMemo(() => {
    const range = discountSliderMax - discountSliderMin;
    if (range <= 0) return 0;
    const normalized = ((currentDiscountAnchorPct - discountSliderMin) / range) * 100;
    return Math.min(Math.max(normalized, 0), 100);
  }, [currentDiscountAnchorPct, discountSliderMax, discountSliderMin]);

  const top10LiveValueToday = useMemo(() => {
    return toNum(topUnsoldShare?.top_10_remaining_value, 0) * liveValueMultiplier;
  }, [liveValueMultiplier, topUnsoldShare, toNum]);

  const top10LiveSharePct = useMemo(() => {
    const totalLive = toNum(totalLiveValueToday, 0);
    const top10Live = toNum(top10LiveValueToday, 0);
    if (!Number.isFinite(totalLive) || totalLive <= 0 || !Number.isFinite(top10Live) || top10Live <= 0) return 0;
    return (top10Live / totalLive) * 100;
  }, [toNum, top10LiveValueToday, totalLiveValueToday]);

  const top10CurrentValueGuidance = useMemo(
    () => getTop10ValueGuidance(top10LiveSharePct),
    [top10LiveSharePct],
  );

  const top10ItemCount = useMemo(() => Math.min(10, Math.max(itemsAvailable, 0)), [itemsAvailable]);
  const remainingItemsOutsideTop10 = useMemo(() => Math.max(itemsAvailable - top10ItemCount, 0), [itemsAvailable, top10ItemCount]);
  const remainingValueOutsideTop10 = useMemo(
    () => Math.max(totalLiveValueToday - top10LiveValueToday, 0),
    [top10LiveValueToday, totalLiveValueToday],
  );

  const salePatternInterpretation = useMemo(
    () => getSalePatternInterpretation(top10LiveSharePct),
    [top10LiveSharePct],
  );

  const topUnsoldByCategory = useMemo(() => {
    const groups = new Map();
    (topUnsoldRows || []).forEach((row) => {
      const category = String(row?.category || 'Uncategorized').trim() || 'Uncategorized';
      const taggedPrice = toNum(row?.projected_price, 0);
      if (!groups.has(category)) {
        groups.set(category, {
          category,
          rows: [],
          totalTaggedValue: 0,
        });
      }
      const group = groups.get(category);
      group.rows.push(row);
      group.totalTaggedValue += taggedPrice;
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        rows: [...group.rows].sort((a, b) => toNum(a?.rank_in_category, 0) - toNum(b?.rank_in_category, 0)),
      }))
      .sort((a, b) => {
        const valueDiff = toNum(b?.totalTaggedValue, 0) - toNum(a?.totalTaggedValue, 0);
        if (valueDiff !== 0) return valueDiff;
        return String(a?.category || '').localeCompare(String(b?.category || ''), undefined, { sensitivity: 'base' });
      });
  }, [topUnsoldRows, toNum]);

  const unsoldCategoryColumnWidthPx = useMemo(() => {
    const labels = [
      'Category',
      ...topUnsoldByCategory.map((group) => String(group?.category || '').trim()),
    ];
    return measureTextColumnWidth(labels, {
      min: TOP_UNSOLD_CATEGORY_MIN_WIDTH,
      max: TOP_UNSOLD_CATEGORY_MAX_WIDTH,
      charWidth: 9,
      font: TOP_UNSOLD_CATEGORY_FONT,
      extraPadding: TOP_UNSOLD_CATEGORY_EXTRA_PADDING,
    });
  }, [topUnsoldByCategory]);

  const unsoldRowGridStyle = useMemo(() => ({
    gridTemplateColumns: `${unsoldCategoryColumnWidthPx}px 13rem auto`,
  }), [unsoldCategoryColumnWidthPx]);

  const toggleUnsoldAccordionCategory = useCallback((categoryName) => {
    setUnsoldAccordionExpanded((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  }, []);

  const totalTaggedValue = useMemo(() => {
    // Keep Sales Stats aligned with Dashboard by preferring select_all_sales total.
    const rpcValue = toNum(selectedSale?.total_asking_price, Number.NaN);
    if (Number.isFinite(rpcValue)) return rpcValue;
    if (rollupTotals.hasRollup) return rollupTotals.totalTagValue + onHoldValue;
    return toNum(overview?.total_per_item_cost_estimate, 0);
  }, [onHoldValue, overview, rollupTotals, selectedSale, toNum]);

  const avgCurrentPrice = useMemo(
    () => safeAverage(totalLiveValueToday, itemsAvailable),
    [itemsAvailable, totalLiveValueToday],
  );

  const avgProposedPrice = useMemo(
    () => safeAverage(estimatedValueAtProposedDiscount, itemsAvailable),
    [estimatedValueAtProposedDiscount, itemsAvailable],
  );

  const avgTaggedPriceTotal = useMemo(
    () => safeAverage(totalTaggedValue, totalItemsCount),
    [totalItemsCount, totalTaggedValue],
  );

  const avgTaggedPriceAvailable = useMemo(
    () => safeAverage(availableFloorTaggedValue, itemsAvailable),
    [availableFloorTaggedValue, itemsAvailable],
  );

  const avgSoldPrice = useMemo(
    () => safeAverage(totalSoldValue, soldItemsCount),
    [soldItemsCount, totalSoldValue],
  );

  const largestAvailableSection = useMemo(
    () => pickHighestValueRow(sectionRows, { nameKey: 'section_name' }, toNum),
    [sectionRows, toNum],
  );

  const largestAvailableSectionName = largestAvailableSection?.section_name || 'N/A';
  const largestAvailableSectionItems = toNum(largestAvailableSection?.active_item_count, 0);
  const largestAvailableSectionCurrentValue = toNum(largestAvailableSection?.remaining_value, 0) * liveValueMultiplier;
  const largestAvailableSectionAvgPrice = safeAverage(
    largestAvailableSectionCurrentValue,
    largestAvailableSectionItems,
  );
  const sectionNameMeasureRef = useRef(null);
  const categoryNameMeasureRef = useRef(null);
  const [shrinkSectionName, setShrinkSectionName] = useState(false);
  const [shrinkCategoryName, setShrinkCategoryName] = useState(false);

  const refreshWrappedNameSizing = useCallback(() => {
    const isWrapped = (el) => {
      if (!el) return false;
      const lineHeight = Number.parseFloat(window.getComputedStyle(el).lineHeight || '0');
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) return false;
      return el.scrollHeight > (lineHeight * 1.2);
    };
    const isHorizontallyOverflowing = (el) => {
      if (!el) return false;
      return el.scrollWidth > el.clientWidth;
    };
    setShrinkSectionName(isWrapped(sectionNameMeasureRef.current));
    setShrinkCategoryName(
      isWrapped(categoryNameMeasureRef.current) || isHorizontallyOverflowing(categoryNameMeasureRef.current),
    );
  }, []);
  const largestAvailableCategory = useMemo(
    () => pickHighestValueRow(categoryRows, { nameKey: 'category' }, toNum),
    [categoryRows, toNum],
  );
  const largestAvailableCategoryName = largestAvailableCategory?.category || 'N/A';
  const largestAvailableCategoryItems = toNum(largestAvailableCategory?.active_item_count, 0);
  const largestAvailableCategoryCurrentValue = toNum(largestAvailableCategory?.remaining_value, 0) * liveValueMultiplier;
  const largestAvailableCategoryAvgPrice = safeAverage(
    largestAvailableCategoryCurrentValue,
    largestAvailableCategoryItems,
  );
  const sectionNameSizeClass = shrinkSectionName ? 'text-lg' : 'text-2xl';
  const categoryNameSizeClass = shrinkCategoryName ? 'text-lg' : 'text-2xl';
  const onHoldItems = onHoldItemsCount;

  useEffect(() => {
    const rafId = window.requestAnimationFrame(refreshWrappedNameSizing);
    window.addEventListener('resize', refreshWrappedNameSizing);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', refreshWrappedNameSizing);
    };
  }, [largestAvailableSectionName, largestAvailableCategoryName, refreshWrappedNameSizing]);

  if (authLoading || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        Loading...
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-4 sm:p-6 mx-auto" style={workingPanelStyle}>
        <div className="mx-auto" style={workingContentStyle}>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="inline-flex items-center gap-1.5 text-2xl font-bold text-slate-900">
                <span>Sale Performance</span>
                <HelpTip text={HELP_TEXT.page} />
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                View sale totals, category performance, section performance, and top unsold items.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => navigate('/estate-sales-pipeline/userdashboard')}
                className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
              >
                Back to Dashboard
              </button>
              <button
                type="button"
                onClick={() => navigate(
                  `/estate-sales-pipeline/sales-transactions${selectedSaleId ? `?saleId=${selectedSaleId}` : ''}`
                )}
                className="h-10 px-4 rounded-lg bg-[#0047AB] text-white font-semibold text-sm hover:bg-[#003B8F]"
              >
                See Sales Transactions
              </button>
            </div>
          </div>

          <div className="mt-3">
            <label id="sales-stats-sale-select-label" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Select Sale
            </label>
            <div
              ref={saleMenuRef}
              className="relative"
              style={{ width: `min(100%, ${saleSelectWidthCh}ch)` }}
            >
              <button
                id="sales-stats-sale-select"
                type="button"
                disabled={loadingSales || sales.length === 0}
                onClick={() => setSaleMenuOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={saleMenuOpen}
                aria-labelledby="sales-stats-sale-select-label sales-stats-sale-select"
                className="w-full h-11 rounded-lg border border-slate-300 px-3 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100 disabled:text-slate-500 inline-flex items-center justify-between gap-3"
              >
                <span className="truncate">
                  {selectedSale?.sale_name || (loadingSales ? 'Loading sales...' : 'Select a sale')}
                </span>
                <span className={`text-slate-600 transition-transform ${saleMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true">
                  ▼
                </span>
              </button>

              {saleMenuOpen && sales.length > 0 && (
                <div
                  role="listbox"
                  aria-labelledby="sales-stats-sale-select-label"
                  className="absolute z-50 left-0 right-0 top-full mt-1 rounded-lg border border-slate-300 bg-white shadow-lg overflow-hidden"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-200">
                    Select a sale
                  </div>
                  <div className="max-h-64 overflow-auto">
                    {sales.map((sale) => {
                      const isSelected = sale.id === selectedSaleId;
                      const saleStart = formatShortDate(sale?.start_date) || 'Start TBD';
                      const saleEnd = formatShortDate(sale?.end_date) || 'End TBD';
                      return (
                        <button
                          key={sale.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => {
                            setSelectedSaleId(sale.id);
                            setSaleMenuOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                            isSelected
                              ? 'bg-blue-50 text-slate-900 font-semibold'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <div className="truncate">{sale.sale_name}</div>
                          <div className="text-xs text-slate-500 font-normal mt-0.5 truncate">{saleStart} - {saleEnd}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {selectedSale && (
          <div className="mt-3">
            <div className="text-xl font-bold text-slate-900 text-center">
              {selectedSale?.sale_name || 'Selected Sale'}
            </div>
            <div className="text-sm text-slate-600 text-center mt-1">
              {formatShortDate(selectedSale?.start_date) || 'Start TBD'}
              {' - '}
              {formatShortDate(selectedSale?.end_date) || 'End TBD'}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mt-4">
          {selectedSale && (
            <div className="flex flex-col">
              <div className="order-1 rounded-xl border border-slate-300 bg-white p-5 shadow-sm">
                <div className="inline-flex items-center gap-1.5 text-xl font-bold text-slate-900">
                  <span>{VALUE_SECTION_TITLE}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">{`Current discount: ${Math.round(discountPct)}%`}</div>
                <div className="mt-2.5 grid grid-cols-1 lg:grid-cols-4 gap-2.5">
                  <div className="rounded-md bg-slate-50 px-2.5 py-1 flex flex-col items-center text-center min-h-[108px]">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Current Available Value</span>
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatCurrency(totalLiveValueToday)}</div>
                    <div className="text-base text-slate-800 mt-2">{formatCount(itemsAvailable)} available items</div>
                    <div className="text-base text-slate-800 mt-0.5">Avg. {formatCurrency(avgCurrentPrice)}</div>
                  </div>
                  <div className="rounded-md bg-slate-50 px-2.5 py-1 flex flex-col items-center text-center min-h-[108px]">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Top 10 Current Value</span>
                      <HelpTip text={HELP_TEXT.top10Value} />
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatCurrency(top10LiveValueToday)}</div>
                    <div className="text-base text-slate-800 mt-2">{formatPercent(top10LiveSharePct)} of current value</div>
                    <div className="text-base text-slate-800 mt-0.5">{top10CurrentValueGuidance}</div>
                  </div>
                  <div className="rounded-md bg-slate-50 px-2.5 py-1 flex flex-col items-center text-center min-h-[108px]">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Largest Section</span>
                    </div>
                    <div className="relative w-full">
                      <div ref={sectionNameMeasureRef} className="absolute inset-x-0 top-0 invisible pointer-events-none text-2xl font-bold leading-tight text-center">
                        {largestAvailableSectionName}
                      </div>
                      <div className={`${sectionNameSizeClass} font-bold tabular-nums text-slate-900 mt-2 leading-tight`}>{largestAvailableSectionName}</div>
                    </div>
                    <div className="text-base text-slate-800 mt-2">{formatCurrency(largestAvailableSectionCurrentValue)} available</div>
                    <div className="text-base text-slate-800 mt-0.5">{formatCount(largestAvailableSectionItems)} available items</div>
                    <div className="text-base text-slate-800 mt-0.5">Avg. {formatCurrency(largestAvailableSectionAvgPrice)}</div>
                  </div>
                  <div className="rounded-md bg-slate-50 px-2.5 py-1 flex flex-col items-center text-center min-h-[108px]">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Largest Category</span>
                    </div>
                    <div className="relative w-full">
                      <div ref={categoryNameMeasureRef} className="absolute inset-x-0 top-0 invisible pointer-events-none text-2xl font-bold leading-tight text-center">
                        {largestAvailableCategoryName}
                      </div>
                      <div className={`${categoryNameSizeClass} font-bold tabular-nums text-slate-900 mt-2 leading-tight`}>{largestAvailableCategoryName}</div>
                    </div>
                    <div className="text-base text-slate-800 mt-2">{formatCurrency(largestAvailableCategoryCurrentValue)} available</div>
                    <div className="text-base text-slate-800 mt-0.5">{formatCount(largestAvailableCategoryItems)} available items</div>
                    <div className="text-base text-slate-800 mt-0.5">Avg. {formatCurrency(largestAvailableCategoryAvgPrice)}</div>
                  </div>
                </div>
              </div>

              <div className="order-2 mt-3 rounded-xl border border-slate-200 bg-slate-50/25 p-3">
                <div className="inline-flex items-center gap-1.5 text-lg font-semibold text-slate-800">
                  <span>Overall Sale Totals</span>
                </div>
                <div className="mt-2 rounded-md border border-slate-200 bg-white overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-5">
                  <div className="px-3.5 lg:px-4 py-2.5 flex flex-col items-center text-center min-h-[116px] lg:border-r lg:border-slate-200">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Total Sale Value</span>
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatCurrency(totalTaggedValue)}</div>
                    <div className="text-base text-slate-800 mt-auto">{formatCount(totalItemsCount)} total items</div>
                    <div className="text-base text-slate-800 mt-0.5">Avg. {formatCurrency(avgTaggedPriceTotal)}</div>
                  </div>
                  <div className="px-3.5 lg:px-4 py-2.5 flex flex-col items-center text-center min-h-[116px] lg:border-r lg:border-slate-200">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Available Tag Value</span>
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatCurrency(availableFloorTaggedValue)}</div>
                    <div className="text-base text-slate-800 mt-auto">{formatCount(itemsAvailable)} tagged items</div>
                    <div className="text-base text-slate-800 mt-0.5">Avg. {formatCurrency(avgTaggedPriceAvailable)}</div>
                  </div>
                  <div className="px-3.5 lg:px-4 py-2.5 flex flex-col items-center text-center min-h-[116px] lg:border-r lg:border-slate-200">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Sales Revenue</span>
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatCurrency(totalSoldValue)}</div>
                    <div className="text-base text-slate-800 mt-auto">{formatCount(soldItemsCount)} sold items</div>
                    <div className="text-base text-slate-800 mt-0.5">Avg. {formatCurrency(avgSoldPrice)}</div>
                  </div>
                  <div className="px-3.5 lg:px-4 py-2.5 flex flex-col items-center text-center min-h-[116px] lg:border-r lg:border-slate-200">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>Sell-Through %</span>
                      <HelpTip text={HELP_TEXT.sellThrough} />
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatPercent(sellThroughPct)}</div>
                    <div className="text-base text-slate-800 mt-auto">{formatCount(soldItemsCount)} sold items</div>
                    <div className="text-base text-slate-800 mt-0.5">{formatCount(itemsAvailable)} available items</div>
                  </div>
                  <div className="px-3.5 lg:px-4 py-2.5 flex flex-col items-center text-center min-h-[116px]">
                    <div className="inline-flex items-center gap-1 text-sm text-slate-600 font-semibold">
                      <span>On Hold Value</span>
                      <HelpTip text={HELP_TEXT.onHoldValue} align="end" />
                    </div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900 mt-1.5">{formatCurrency(onHoldValue)}</div>
                    <div className="text-base text-slate-800 mt-auto">{formatCount(onHoldItems)} on hold items</div>
                    <div className="text-base text-slate-800 mt-0.5">Off floor</div>
                  </div>
                  </div>
                </div>
              </div>

              <div className="order-3 mt-3 rounded-xl border border-slate-200 bg-white p-3">
                <div className="text-sm font-semibold text-slate-800">What&apos;s left in this sale</div>
                <div className="mt-2 text-sm text-slate-700 leading-snug">
                  Top 10 items account for {formatPercent(top10LiveSharePct)} of the remaining value, or {formatCurrency(top10LiveValueToday)} of {formatCurrency(totalLiveValueToday)}.
                </div>
                <div className="mt-2 text-sm text-slate-700 leading-snug">
                  The other {formatCount(remainingItemsOutsideTop10)} available items account for the remaining {formatCurrency(remainingValueOutsideTop10)} of value.
                </div>
                <div className="mt-2 text-sm text-slate-700 leading-snug">
                  {salePatternInterpretation}
                </div>
              </div>

            </div>
          )}

          <div className="mt-3 text-xs text-slate-500">
            Open a section below to review category, section, unsold item, or discount details.
          </div>

          <div className="mt-2 grid grid-cols-1 gap-4">
            <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setCategoryExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                  aria-expanded={categoryExpanded}
                  aria-controls="category-breakdown-table"
                >
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-lg font-bold text-slate-900">
                      <span>Category Breakdown</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">Tagged-price view of what has sold and what remains by category.</div>
                  </div>
                  <CollapseChevron expanded={categoryExpanded} />
                </button>
              </div>
              {categoryExpanded && (
              <div id="category-breakdown-table" className="overflow-auto max-h-[65vh] lg:overflow-visible lg:max-h-none">
                <table ref={categoryTableMeasureRef} className="w-fit table-auto text-[15.3px]">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th
                        className={`sticky top-0 left-0 z-30 text-left px-1.5 py-1.5 font-bold whitespace-nowrap ${categoryHeaderColorClass('category', true)}`}
                        style={categoryColumnWidthStyle}
                      >
                        <button
                          type="button"
                          onClick={() => toggleCategorySort('category')}
                          className="w-full inline-flex items-center justify-start gap-1"
                        >
                          <span>Category</span>
                          {renderSortArrow(categorySort, 'category')}
                        </button>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 py-1.5 font-semibold ${categoryHeaderColorClass('sold_item_count')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('sold_item_count')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span>Sold</span>
                            {renderSortArrow(categorySort, 'sold_item_count')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 py-1.5 font-semibold ${categoryHeaderColorClass('active_item_count')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('active_item_count')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span>Active</span>
                            {renderSortArrow(categorySort, 'active_item_count')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1.5 sm:px-2 py-1.5 font-semibold whitespace-normal leading-tight ${categoryHeaderColorClass('sell_through_pct')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('sell_through_pct')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Sell-Through</span>
                            {renderSortArrow(categorySort, 'sell_through_pct')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1.5 sm:px-2 py-1.5 font-semibold whitespace-normal leading-tight ${categoryHeaderColorClass('projected_value')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('projected_value')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Tag Value</span>
                            {renderSortArrow(categorySort, 'projected_value')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1.5 sm:px-2 py-1.5 font-semibold whitespace-normal leading-tight ${categoryHeaderColorClass('sold_value')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('sold_value')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Sales Revenue</span>
                            {renderSortArrow(categorySort, 'sold_value')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 sm:px-1.5 py-1.5 font-bold whitespace-normal leading-tight ${categoryHeaderColorClass('remaining_value', true)}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('remaining_value')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Unsold Tag Value</span>
                            {renderSortArrow(categorySort, 'remaining_value')}
                          </button>
                          <HelpTip text={HELP_TEXT.unsoldTaggedCol} />
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 sm:px-1.5 py-1.5 font-semibold whitespace-normal leading-tight ${categoryHeaderColorClass('average_value_per_item')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategorySort('average_value_per_item')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Avg Tag Price</span>
                            {renderSortArrow(categorySort, 'average_value_per_item')}
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCategoryRows.map((row) => {
                      const rowKey = String(row.category || '');
                      const isSelected = selectedCategoryRowKey === rowKey;
                      const isCategorySortCol = categorySort.key === 'category';
                      return (
                      <tr
                        key={row.category}
                        className={`border-t border-slate-100 cursor-pointer ${isSelected ? 'bg-blue-50' : 'bg-white'}`}
                        onClick={() => setSelectedCategoryRowKey((prev) => (prev === rowKey ? null : rowKey))}
                        aria-selected={isSelected}
                      >
                        <td
                          className={`sticky left-0 z-10 px-1.5 py-1.5 text-left text-slate-900 font-semibold whitespace-nowrap ${(isSelected || isCategorySortCol) ? 'bg-blue-50' : 'bg-white'}`}
                          style={categoryColumnWidthStyle}
                        >
                          {row.category}
                        </td>
                        <td className={`px-1 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('sold_item_count')}`}>{formatCount(row.sold_item_count)}</td>
                        <td className={`px-0 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('active_item_count')}`}>{formatCount(row.active_item_count)}</td>
                        <td className={`px-1.5 sm:px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('sell_through_pct')}`}>{formatPercent(row.sell_through_pct)}</td>
                        <td className={`px-1.5 sm:px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('projected_value')}`}>{formatCurrency(row.projected_value)}</td>
                        <td className={`px-1.5 sm:px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('sold_value')}`}>{formatCurrency(row.sold_value)}</td>
                        <td className={`px-1 sm:px-1.5 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('remaining_value', true)}`}>{formatCurrency(row.remaining_value)}</td>
                        <td className={`px-1 sm:px-1.5 py-1.5 text-center tabular-nums whitespace-nowrap ${categoryCellColorClass('average_value_per_item')}`}>{formatCurrency(row.average_value_per_item)}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
                {!loadingStats && visibleCategoryRows.length === 0 && (
                  <div className="px-4 py-5 text-sm text-slate-500">No category data found for this sale.</div>
                )}
              </div>
              )}
            </div>

            <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setSectionExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                  aria-expanded={sectionExpanded}
                  aria-controls="section-breakdown-table"
                >
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-lg font-bold text-slate-900">
                      <span>Section Breakdown</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">Tagged-price view of what has sold and what remains by section.</div>
                  </div>
                  <CollapseChevron expanded={sectionExpanded} />
                </button>
              </div>
              {sectionExpanded && (
              <div id="section-breakdown-table" className="overflow-auto max-h-[65vh] lg:overflow-visible lg:max-h-none">
                <table ref={sectionTableMeasureRef} className="w-fit table-auto text-[15.3px]">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th
                        className={`sticky top-0 left-0 z-30 text-left px-1.5 py-1.5 font-bold whitespace-nowrap ${sectionHeaderColorClass('section_name', true)}`}
                        style={sectionColumnWidthStyle}
                      >
                        <div className="w-full inline-flex items-center justify-start gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('section_name')}
                            className="inline-flex items-center justify-start gap-1"
                          >
                            <span>Section</span>
                            {renderSortArrow(sectionSort, 'section_name')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 py-1.5 font-semibold ${sectionHeaderColorClass('sold_item_count')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('sold_item_count')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span>Sold</span>
                            {renderSortArrow(sectionSort, 'sold_item_count')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-0 py-1.5 font-semibold ${sectionHeaderColorClass('active_item_count')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('active_item_count')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span>Active</span>
                            {renderSortArrow(sectionSort, 'active_item_count')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1.5 sm:px-2 py-1.5 font-semibold whitespace-normal leading-tight ${sectionHeaderColorClass('sell_through_pct')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('sell_through_pct')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Sell-Through</span>
                            {renderSortArrow(sectionSort, 'sell_through_pct')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1.5 sm:px-2 py-1.5 font-semibold whitespace-normal leading-tight ${sectionHeaderColorClass('projected_value')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('projected_value')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Tag Value</span>
                            {renderSortArrow(sectionSort, 'projected_value')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1.5 sm:px-2 py-1.5 font-semibold whitespace-normal leading-tight ${sectionHeaderColorClass('sold_value')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('sold_value')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Sales Revenue</span>
                            {renderSortArrow(sectionSort, 'sold_value')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 sm:px-1.5 py-1.5 font-bold whitespace-normal leading-tight ${sectionHeaderColorClass('remaining_value', true)}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('remaining_value')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Unsold Tag Value</span>
                            {renderSortArrow(sectionSort, 'remaining_value')}
                          </button>
                        </div>
                      </th>
                      <th className={`sticky top-0 z-20 text-center px-1 sm:px-1.5 py-1.5 font-semibold whitespace-normal leading-tight ${sectionHeaderColorClass('average_value_per_item')}`}>
                        <div className="w-full inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleSectionSort('average_value_per_item')}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <span className="inline-block whitespace-normal xl:whitespace-nowrap">Avg Tag Price</span>
                            {renderSortArrow(sectionSort, 'average_value_per_item')}
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSectionRows.map((row) => {
                      const rowKey = String(row.section_id || row.section_name || '');
                      const isSelected = selectedSectionRowKey === rowKey;
                      const isSectionSortCol = sectionSort.key === 'section_name';
                      return (
                      <tr
                        key={row.section_id || row.section_name}
                        className={`border-t border-slate-100 cursor-pointer ${isSelected ? 'bg-blue-50' : 'bg-white'}`}
                        onClick={() => setSelectedSectionRowKey((prev) => (prev === rowKey ? null : rowKey))}
                        aria-selected={isSelected}
                      >
                        <td
                          className={`sticky left-0 z-10 px-1.5 py-1.5 text-left text-slate-900 font-semibold whitespace-nowrap ${(isSelected || isSectionSortCol) ? 'bg-blue-50' : 'bg-white'}`}
                          style={sectionColumnWidthStyle}
                        >
                          {row.section_name}
                        </td>
                        <td className={`px-1 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('sold_item_count')}`}>{formatCount(row.sold_item_count)}</td>
                        <td className={`px-0 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('active_item_count')}`}>{formatCount(row.active_item_count)}</td>
                        <td className={`px-1.5 sm:px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('sell_through_pct')}`}>{formatPercent(row.sell_through_pct)}</td>
                        <td className={`px-1.5 sm:px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('projected_value')}`}>{formatCurrency(row.projected_value)}</td>
                        <td className={`px-1.5 sm:px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('sold_value')}`}>{formatCurrency(row.sold_value)}</td>
                        <td className={`px-1 sm:px-1.5 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('remaining_value', true)}`}>{formatCurrency(row.remaining_value)}</td>
                        <td className={`px-1 sm:px-1.5 py-1.5 text-center tabular-nums whitespace-nowrap ${sectionCellColorClass('average_value_per_item')}`}>{formatCurrency(row.average_value_per_item)}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
                {!loadingStats && visibleSectionRows.length === 0 && (
                  <div className="px-4 py-5 text-sm text-slate-500">No section data found for this sale.</div>
                )}
              </div>
              )}
            </div>
          </div>

          <div className="mt-5 border border-slate-200 rounded-xl bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => setTopUnsoldExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between gap-3 text-left"
                aria-expanded={topUnsoldExpanded}
                aria-controls="top-unsold-by-category-accordion"
              >
                <div>
                  <div className="inline-flex items-center gap-1.5 text-lg font-bold text-slate-900">
                    <span>Top 5 Unsold Items by Category</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">Highest priced unsold items in each category.</div>
                </div>
                <CollapseChevron expanded={topUnsoldExpanded} />
              </button>
            </div>
            {topUnsoldExpanded && (
            <div id="top-unsold-by-category-accordion" className="divide-y divide-slate-100">
              {topUnsoldByCategory.map((group) => {
                const isExpanded = Boolean(unsoldAccordionExpanded[group.category]);
                return (
                  <div key={group.category}>
                    <button
                      type="button"
                      onClick={() => toggleUnsoldAccordionCategory(group.category)}
                      className="w-full px-4 py-2 grid items-center gap-1.5 text-left hover:bg-slate-50 transition-colors"
                      style={unsoldRowGridStyle}
                      aria-expanded={isExpanded}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="text-sm sm:text-base font-semibold text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis">{group.category}</div>
                      </div>
                      <div className="flex w-full items-center justify-end gap-2 text-slate-600 tabular-nums whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-slate-500">Top 5 Items =</span>
                        <span className="text-sm sm:text-base font-semibold text-slate-800">{formatCurrency(group.totalTaggedValue)}</span>
                      </div>
                      <div className="flex items-center justify-end shrink-0 pl-1">
                        <CollapseChevron expanded={isExpanded} />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3">
                        <div className="overflow-x-auto rounded-lg border border-slate-200">
                          <table className="w-fit min-w-max table-auto text-[15.3px]">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <th className="text-center px-2 py-2 font-semibold whitespace-nowrap">
                                  <span className="inline-flex items-center justify-center gap-1">
                                    <span>Rank</span>
                                  </span>
                                </th>
                                <th className="text-left px-2 py-2 font-semibold min-w-[20rem]">
                                  <span className="inline-flex items-center gap-1">
                                    <span>Item Title</span>
                                  </span>
                                </th>
                                <th className="text-center px-4 py-2 font-semibold whitespace-nowrap">
                                  <span className="inline-flex items-center justify-center gap-1">
                                    <span>Tag Price</span>
                                  </span>
                                </th>
                                <th className="text-center px-4 py-2 font-semibold whitespace-nowrap">
                                  <span className="inline-flex items-center justify-center gap-1">
                                    <span>Today&apos;s Price</span>
                                  </span>
                                </th>
                                <th className="text-left pl-5 pr-2 py-2 font-semibold whitespace-nowrap">
                                  <span className="inline-flex items-center gap-1">
                                    <span>Section</span>
                                  </span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.rows.map((row) => (
                                <tr key={`${group.category}-${row.inventory_item_id}-${row.rank_in_category}`} className="border-t border-slate-100">
                                  <td className="px-2 py-1.5 text-center text-slate-700 tabular-nums whitespace-nowrap">{formatCount(row.rank_in_category)}</td>
                                  <td className="px-2 py-1.5 text-slate-800 min-w-[20rem]">{row.title}</td>
                                  <td className="px-4 py-1.5 text-center text-slate-700 tabular-nums whitespace-nowrap">{formatCurrency(row.projected_price)}</td>
                                  <td className="px-4 py-1.5 text-center text-slate-700 tabular-nums whitespace-nowrap">{formatCurrency(toNum(row.projected_price, 0) * liveValueMultiplier)}</td>
                                  <td className="pl-5 pr-2 py-1.5 text-slate-700 whitespace-nowrap">{row.section_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {!loadingStats && topUnsoldByCategory.length === 0 && (
                <div className="px-4 py-5 text-sm text-slate-500">No unsold active items found for this sale.</div>
              )}
            </div>
            )}
          </div>

          {(loadingSales || loadingStats) && (
            <div className="mt-4 text-sm text-slate-500">Loading sales statistics...</div>
          )}

          {selectedSale && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setDiscountCalculatorExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                  aria-expanded={discountCalculatorExpanded}
                  aria-controls="discount-calculator-panel"
                >
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-lg font-bold text-slate-900">
                      <span>Test a New Discount</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      Test how a new discount changes item prices and remaining value.
                    </div>
                  </div>
                  <CollapseChevron expanded={discountCalculatorExpanded} />
                </button>
              </div>
              {discountCalculatorExpanded && (
                <div id="discount-calculator-panel" className="p-4">
                  <div className="space-y-3 w-full">
                    <div>
                      <div className="text-sm font-semibold text-slate-700">Choose a discount</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {discountPresetButtons.map((button) => {
                          const isActive = proposedDiscountPct === button.value;
                          return (
                            <button
                              key={`${button.label}-${button.value}`}
                              type="button"
                              onClick={() => setTryDiscountPct(button.value)}
                              className={`px-3 py-1.5 rounded-md border text-sm font-semibold tabular-nums transition-colors ${
                                isActive
                                  ? 'border-blue-600 bg-blue-600 text-white'
                                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              {button.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="w-full">
                        <div className="flex items-end justify-between gap-2 mb-1">
                          <div className="text-sm font-semibold text-slate-700">Slider discount</div>
                          <div className="text-2xl font-bold text-slate-900 tabular-nums leading-none">{proposedDiscountPct}%</div>
                        </div>
                        <div className="relative mt-2">
                          <input
                            type="range"
                            min={discountSliderMin}
                            max={discountSliderMax}
                            step={discountSliderStep}
                            value={proposedDiscountPct}
                            onChange={(event) => setTryDiscountPct(event.target.value)}
                            className="w-full accent-blue-600 cursor-pointer"
                            aria-label="Adjust discount"
                          />
                          {roundedCurrentDiscountPct > 0 && (
                            <span
                              className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-5 w-[2px] bg-slate-800 rounded"
                              style={{ left: `calc(${currentDiscountMarkerPct}% - 1px)` }}
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-slate-500 tabular-nums">
                          <span>{discountSliderMin}%</span>
                          <span>{discountSliderMax}%</span>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:justify-start">
                          <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                            <div className="grid grid-cols-2 gap-3 text-center">
                              <div className="grid grid-rows-[auto_auto_auto]">
                                <div className="text-xs font-semibold text-slate-600 min-h-[2rem] flex flex-col items-center justify-end leading-tight">
                                  <span>Average Tag</span>
                                  <span>Price</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{formatCurrency(avgTaggedPriceAvailable)}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">Before discount</div>
                              </div>
                              <div className="grid grid-rows-[auto_auto_auto]">
                                <div className="text-xs font-semibold text-slate-600 min-h-[2rem] flex flex-col items-center justify-end leading-tight">
                                  <span>{proposedDiscountPct === currentDiscountSelectionPct ? 'Current' : 'New'}</span>
                                  <span>Average Price</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{formatCurrency(avgProposedPrice)}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">{formatDiscountSupportText(proposedDiscountPct)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                            <div className="grid grid-cols-2 gap-3 text-center">
                              <div className="grid grid-rows-[auto_auto_auto]">
                                <div className="text-xs font-semibold text-slate-600 min-h-[2rem] flex flex-col items-center justify-end leading-tight">
                                  <span>Current Available</span>
                                  <span>Value</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{formatCurrency(totalLiveValueToday)}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">{formatDiscountSupportText(roundedCurrentDiscountPct)}</div>
                              </div>
                              <div className="grid grid-rows-[auto_auto_auto]">
                                <div className="text-xs font-semibold text-slate-600 min-h-[2rem] flex flex-col items-center justify-end leading-tight">
                                  <span>{proposedDiscountPct <= 0 ? 'Tag Available' : 'New Available'}</span>
                                  <span>Value</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{formatCurrency(estimatedValueAtProposedDiscount)}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">{formatDiscountSupportText(proposedDiscountPct)}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default SalesStats;
